// frontend/src/pages/ConfirmDeletion.jsx
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ShieldAlert, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { apiClient } from '../api/client';
import { useLanguageStore } from '../store/useLanguageStore';

export const ConfirmDeletion = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const langParam = searchParams.get('lang');
  const { t, setLanguage, language } = useLanguageStore();

  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');

  useEffect(() => {
    if (langParam && ['es', 'en', 'pt'].includes(langParam)) {
      setLanguage(langParam);
    }
  }, [langParam, setLanguage]);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(t('data_erasure.token_expired'));
      return;
    }

    const confirm = async () => {
      try {
        const res = await apiClient.post(`/data-erasure/confirm/${token}`);
        setStatus('success');
        setMessage(res.data.message);
        setScheduledDate(res.data.scheduledDate || '');
      } catch (err) {
        setStatus('error');
        const detail = err.response?.data?.detail;
        setMessage(
          typeof detail === 'string'
            ? detail
            : t('data_erasure.confirm_error')
        );
      }
    };

    confirm();
  }, [token, t]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
        {/* Icon */}
        {status === 'loading' && (
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-6" />
        )}
        {status === 'success' && (
          <CheckCircle2 className="w-16 h-16 text-amber-500 mx-auto mb-6" />
        )}
        {status === 'error' && (
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
        )}

        {/* Title */}
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          {status === 'loading' && t('data_erasure.confirm_page_title')}
          {status === 'success' && t('data_erasure.confirm_success')}
          {status === 'error' && t('data_erasure.confirm_error')}
        </h1>

        {/* Message */}
        <p className="text-gray-600 mb-6 leading-relaxed">
          {message}
        </p>

        {/* Scheduled date badge */}
        {status === 'success' && scheduledDate && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center gap-2 text-amber-700">
              <ShieldAlert className="w-5 h-5" />
              <span className="font-semibold">
                {t('data_erasure.scheduled_for')}: {scheduledDate}
              </span>
            </div>
          </div>
        )}

        {/* Back to login */}
        <Link
          to="/login"
          className="inline-block mt-4 text-blue-600 hover:text-blue-800 font-medium"
        >
          {t('data_erasure.back_to_login')}
        </Link>
      </div>
    </div>
  );
}
